/**
 * @file ソート可能コンポーネント用の統合ユーティリティ関数
 * アイテムの分類、順序管理、パスマッチングなどすべてのユーティリティを集約
 */

import type { AnySidebarItem } from "./sortable-types";
import { SPECIAL_VIEW_IDS } from "./sortable-types";

// ============================================================================
// アイテム分類ユーティリティ
// ============================================================================

/**
 * アイテムをソート可能/不可で分類
 */
export function categorizeItems<T extends AnySidebarItem>(items: T[]) {
  const sortable: T[] = [];
  const nonSortable: T[] = [];

  for (const item of items) {
    if (item.sortable !== false) {
      sortable.push(item);
    } else {
      nonSortable.push(item);
    }
  }

  return { sortable, nonSortable };
}

/**
 * 特別なビューアイテムを抽出
 */
export function extractSpecialItems<T extends AnySidebarItem>(
  nonSortableItems: T[],
) {
  let allItem: T | undefined;
  let sessionExplorerItem: T | undefined;
  let settingsItem: T | undefined;
  const otherItems: T[] = [];

  for (const item of nonSortableItems) {
    if (item.isAllCard) {
      allItem = item;
    } else if (item.id === SPECIAL_VIEW_IDS.SESSION_EXPLORER) {
      sessionExplorerItem = item;
    } else if (item.id === SPECIAL_VIEW_IDS.SETTINGS) {
      settingsItem = item;
    } else {
      otherItems.push(item);
    }
  }

  return {
    allItem,
    sessionExplorerItem,
    settingsItem,
    otherItems,
  };
}

/**
 * アイテムを適切な順序で再構築
 * All → その他非ソート可能 → ソート可能 → Session Explorer → Settings
 */
export function reconstructItemOrder<T extends AnySidebarItem>(
  sortableItems: T[],
  nonSortableItems: T[],
): T[] {
  const { allItem, sessionExplorerItem, settingsItem, otherItems } =
    extractSpecialItems(nonSortableItems);

  const result: T[] = [];

  if (allItem) result.push(allItem);
  result.push(...otherItems);
  result.push(...sortableItems);
  if (sessionExplorerItem) result.push(sessionExplorerItem);
  if (settingsItem) result.push(settingsItem);

  return result;
}

// ============================================================================
// 順序管理ユーティリティ
// ============================================================================

// OrderMapキャッシュ（パフォーマンス最適化）WeakMapで自動ガベージコレクション
const orderMapCache = new WeakMap<string[], Map<string, number>>();

/**
 * 保存されたID順序に基づいてビューを並び替える共通関数（キャッシュ最適化済み）
 * Map生成オーバーヘッドを削減し、メモリ効率とパフォーマンスを最適化
 * @param views - 元のビュー配列
 * @param storedOrderIds - 保存されているIDの順序
 * @returns 並び替えられたビュー配列
 */
export function reorderViewsByIds<T extends { id: string }>(
  views: T[],
  storedOrderIds: string[] | undefined,
): T[] {
  // 保存された順序がない場合は元の配列をそのまま返す
  if (!storedOrderIds || storedOrderIds.length === 0) {
    return views;
  }

  // キャッシュされたマップを再利用（パフォーマンス向上）
  let orderMap = orderMapCache.get(storedOrderIds);
  if (!orderMap) {
    // ID -> インデックスのマップを作成（O(m)）
    orderMap = new Map(storedOrderIds.map((id, index) => [id, index]));
    orderMapCache.set(storedOrderIds, orderMap);
  }

  // ビューをソート：
  // - 保存された順序にあるものは、その順序に従う
  // - 新規ビュー（順序にないもの）は最後に配置
  return views.toSorted((a, b) => {
    const indexA = orderMap!.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const indexB = orderMap!.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });
}

/**
 * ソート可能なアイテムのIDのみを抽出する関数
 * @param items - アイテムの配列
 * @returns ソート可能なアイテムのIDの配列
 */
export function extractSortableIds<
  T extends { id: string; sortable?: boolean },
>(items: T[]): string[] {
  return items.filter((item) => item.sortable !== false).map((item) => item.id);
}

// ============================================================================
// パスマッチングユーティリティ
// ============================================================================

/**
 * 正規表現パターンの特殊文字をエスケープ
 */
const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

function escapeRegexPattern(str: string): string {
  return str.replace(REGEX_SPECIAL_CHARS, "\\$&");
}

/**
 * パスがアクティブパターンにマッチするかチェック（メモ化対応）
 */
const patternCache = new Map<string, RegExp>();

export function isPathActive(pathname: string, href: string): boolean {
  const escapedHref = escapeRegexPattern(href);
  const pattern = `.*${escapedHref}$`;

  let regex = patternCache.get(pattern);
  if (!regex) {
    regex = new RegExp(pattern);
    patternCache.set(pattern, regex);
  }

  return regex.test(pathname);
}
